import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { partnerSettingsKeys } from "@/components/pages/partner-integration-settings/hooks/partner-settings.hooks";
import {
  getPartnerAnalyticsPeriodParams,
  mapPartnerRevenueAnalyticsToChartPoints,
} from "@/components/pages/guesteria-partners/utils/partnerAnalyticsPeriod";
import { useHotel } from "@/hooks/useHotel";
import { partnerSettingsService } from "@/services/partner-settings-service";
import type {
  PartnerRevenueAnalyticsApiResponse,
  PartnerRevenueAnalyticsChartPoint,
  PartnerRevenueAnalyticsPeriod,
} from "@/types/partner-revenue-analytics";
import type { PartnerProviderType } from "@/types/partner-settings";
import { is404 } from "@/utils/httpErrors";

const emptyAnalytics = (
  bucket: PartnerRevenueAnalyticsApiResponse["bucket"],
): PartnerRevenueAnalyticsApiResponse => ({
  bucket,
  points: [],
});

export const usePartnerRevenueAnalytics = (
  period: PartnerRevenueAnalyticsPeriod,
  provider: PartnerProviderType | undefined,
  enabled = true,
) => {
  const { hotelId } = useHotel();
  const { i18n } = useTranslation();
  const periodParams = useMemo(
    () => getPartnerAnalyticsPeriodParams(period),
    [period],
  );

  const query = useQuery<PartnerRevenueAnalyticsApiResponse>({
    queryKey: partnerSettingsKeys.revenueAnalytics(
      hotelId || "",
      period,
      provider || "",
    ),
    enabled: Boolean(hotelId) && Boolean(provider) && enabled,
    queryFn: async () => {
      try {
        return await partnerSettingsService.getPartnerRevenueAnalytics(
          hotelId!,
          {
            ...periodParams,
            provider,
          },
        );
      } catch (e) {
        if (is404(e)) {
          return emptyAnalytics(periodParams.bucket);
        }
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });

  const chartPoints = useMemo<PartnerRevenueAnalyticsChartPoint[]>(() => {
    if (!query.data) {
      return [];
    }
    return mapPartnerRevenueAnalyticsToChartPoints(
      query.data,
      i18n.language || "en",
      (key, options) => i18n.t(key, options),
    );
  }, [query.data, i18n]);

  const currency =
    query.data?.points.find((point) => point.currency)?.currency ?? "EUR";

  return {
    ...query,
    chartPoints,
    currency,
    bucket: query.data?.bucket,
    periodParams,
  };
};
